import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  LexicalEditor as LexicalEditorType,
  LexicalNode,
} from 'lexical'
import { MouseEvent as ReactMouseEvent, useCallback, useState } from 'react'

import { $isCustomEncryptedNode } from '../../../components/editor/lexical/nodes'

export function useEncryptedSectionActions(getEditor?: () => LexicalEditorType | null | undefined) {
  const [decryptionModalOpen, setDecryptionModalOpen] = useState(false)
  const [encryptedContentToDecrypt, setEncryptedContentToDecrypt] = useState('')
  const [currentEncryptionId, setCurrentEncryptionId] = useState('')
  const [mnemonicMap, setMnemonicMap] = useState<Record<string, string>>({})

  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const [contextMenuEncryptionId, setContextMenuEncryptionId] = useState('')
  /** Which kind the right-clicked passage is — it decides what the menu may offer. */
  const [contextMenuKind, setContextMenuKind] = useState<{ character?: string; authorHeld?: true }>({})

  const handleEncryptedClick = useCallback((encryptedText: string, encryptionId: string) => {
    setEncryptedContentToDecrypt(encryptedText)
    setCurrentEncryptionId(encryptionId)
    setDecryptionModalOpen(true)
  }, [])

  const handleDecryptSuccess = useCallback(
    (_decryptedText: string, mnemonic: string) => {
      setMnemonicMap((prev) => ({
        ...prev,
        [currentEncryptionId]: mnemonic,
      }))
    },
    [currentEncryptionId],
  )

  const handleEncryptedContextMenu = useCallback(
    (event: ReactMouseEvent, encryptionId: string, kind?: { character?: string; authorHeld?: true }) => {
      event.preventDefault()
      setContextMenuPosition({ top: event.clientY, left: event.clientX })
      setContextMenuEncryptionId(encryptionId)
      setContextMenuKind(kind ?? {})
    },
    [],
  )

  const handleRemoveEncryption = useCallback(() => {
    if (!contextMenuEncryptionId) {
      return
    }
    const editor = getEditor?.()
    if (!editor) {
      return
    }

    editor.update(() => {
      const visit = (node: LexicalNode): boolean => {
        if ($isCustomEncryptedNode(node) && node.getEncryptionId() === contextMenuEncryptionId) {
          const text = node.getEncryptedText()

          // Multi-paragraph encryption stores \n\n between paragraphs.
          // Restore original paragraph structure on decrypt.
          if (text.includes('\n\n')) {
            const paragraphs = text.split('\n\n')
            const parent = node.getParent()

            if (parent && $isElementNode(parent)) {
              // Put first paragraph's text in place of the encrypted node
              const firstTextNode = $createTextNode(paragraphs[0])
              node.replace(firstTextNode)

              // Insert new paragraph nodes after the parent for remaining paragraphs
              let insertAfter = parent
              for (let p = 1; p < paragraphs.length; p++) {
                const newParagraph = $createParagraphNode()
                if (paragraphs[p]) {
                  newParagraph.append($createTextNode(paragraphs[p]))
                }
                insertAfter.insertAfter(newParagraph)
                insertAfter = newParagraph
              }
            } else {
              // Fallback: just replace inline
              node.replace($createTextNode(text))
            }
          } else {
            node.replace($createTextNode(text))
          }
          return true
        }
        if ($isElementNode(node)) {
          for (const child of node.getChildren()) {
            if (visit(child)) {
              return true
            }
          }
        }
        return false
      }
      visit($getRoot())
    })
  }, [contextMenuEncryptionId, getEditor])

  return {
    decryptionModalOpen,
    encryptedContentToDecrypt,
    mnemonicMap,
    contextMenuPosition,
    contextMenuEncryptionId,
    contextMenuKind,
    setDecryptionModalOpen,
    setContextMenuPosition,
    handleEncryptedClick,
    handleDecryptSuccess,
    handleEncryptedContextMenu,
    handleRemoveEncryption,
  }
}
